import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useGuestAuth } from '../../lib/guest-auth-context';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '../ui/sheet';
import {
  Hotel,
  LogIn,
  UserPlus,
  Menu,
  User,
  Calendar,
  Ticket,
  Heart,
  LogOut,
  Search,
  Home,
  FileText,
  Flower2,
  LayoutDashboard,
  Award,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const navItems = [
  { name: 'Home', href: '/portal', icon: Home },
  { name: 'Search', href: '/portal', icon: Search, action: 'search' },
  { name: 'Spa & Wellness', href: '/portal/spa', icon: Flower2 },
  { name: 'Events', href: '/portal/events', icon: Calendar },
];

const authenticatedNavItems = [
  { name: 'Dashboard', href: '/portal/dashboard', icon: LayoutDashboard },
  { name: 'My Reservations', href: '/portal/dashboard', icon: Calendar },
  { name: 'Smart Room', href: '/portal/smart-room', icon: Home },
  { name: 'Rewards', href: '/portal/loyalty', icon: Award },
];

export default function GuestLayout() {
  const { guest, isAuthenticated, logout, isLoading } = useGuestAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/portal');
  };

  const getInitials = () => {
    if (!guest) return 'G';
    const first = guest.first_name?.[0] || '';
    const last = guest.last_name?.[0] || '';
    return (first + last).toUpperCase() || 'G';
  };

  const isActive = (href) => location.pathname === href;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20" data-testid="guest-layout">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          {/* Logo */}
          <Link to="/portal" className="flex items-center gap-2 font-bold text-xl">
            <Hotel className="h-7 w-7 text-primary" />
            <span className="hidden sm:inline">HMS Hotels</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "px-4 py-2 rounded-md text-sm font-medium transition-colors",
                  isActive(item.href)
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                )}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Auth Section */}
          <div className="flex items-center gap-2">
            {isLoading ? (
              <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />
            ) : isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                    <Avatar className="h-10 w-10 border-2 border-primary/20">
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                        {getInitials()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">
                        {guest?.first_name} {guest?.last_name}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {guest?.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/portal/dashboard')}>
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/portal/dashboard')}>
                    <Calendar className="mr-2 h-4 w-4" />
                    My Reservations
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/portal/smart-room')}>
                    <Home className="mr-2 h-4 w-4" />
                    Smart Room
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/portal/loyalty')}>
                    <Award className="mr-2 h-4 w-4" />
                    Rewards
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/portal/profile')}>
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => navigate('/portal/login')}>
                  <LogIn className="h-4 w-4 mr-2" />
                  Sign In
                </Button>
                <Button size="sm" onClick={() => navigate('/portal/register')}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Register
                </Button>
              </div>
            )}

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <div className="flex flex-col gap-4 mt-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Hotel className="h-6 w-6 text-primary" />
                    <span className="font-bold text-lg">HMS Hotels</span>
                  </div>
                  
                  {isAuthenticated && (
                    <div className="p-3 bg-muted/50 rounded-lg mb-4">
                      <p className="font-medium">{guest?.first_name} {guest?.last_name}</p>
                      <p className="text-sm text-muted-foreground">{guest?.email}</p>
                    </div>
                  )}

                  <nav className="flex flex-col gap-1">
                    {navItems.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium",
                          isActive(item.href)
                            ? "bg-primary/10 text-primary"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted"
                        )}
                      >
                        <item.icon className="h-4 w-4" />
                        {item.name}
                      </Link>
                    ))}
                    
                    {isAuthenticated && (
                      <>
                        <div className="my-2 border-t" />
                        {authenticatedNavItems.map((item) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className={cn(
                              "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium",
                              isActive(item.href)
                                ? "bg-primary/10 text-primary"
                                : "text-muted-foreground hover:text-foreground hover:bg-muted"
                            )}
                          >
                            <item.icon className="h-4 w-4" />
                            {item.name}
                          </Link>
                        ))}
                      </>
                    )}
                  </nav>

                  <div className="mt-auto pt-4 border-t">
                    {isAuthenticated ? (
                      <Button variant="outline" className="w-full" onClick={handleLogout}>
                        <LogOut className="h-4 w-4 mr-2" />
                        Sign Out
                      </Button>
                    ) : (
                      <div className="flex flex-col gap-2">
                        <Button variant="outline" className="w-full" onClick={() => { navigate('/portal/login'); setMobileMenuOpen(false); }}>
                          <LogIn className="h-4 w-4 mr-2" />
                          Sign In
                        </Button>
                        <Button className="w-full" onClick={() => { navigate('/portal/register'); setMobileMenuOpen(false); }}>
                          <UserPlus className="h-4 w-4 mr-2" />
                          Register
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t bg-muted/30 mt-auto">
        <div className="container mx-auto px-4 py-8">
          <div className="grid gap-8 md:grid-cols-4">
            <div>
              <div className="flex items-center gap-2 font-bold text-lg mb-4">
                <Hotel className="h-6 w-6 text-primary" />
                HMS Hotels
              </div>
              <p className="text-sm text-muted-foreground">
                Experience luxury and comfort at our premium hotels across Ethiopia.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Quick Links</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/portal" className="hover:text-foreground">Home</Link></li>
                <li><Link to="/portal/spa" className="hover:text-foreground">Spa & Wellness</Link></li>
                <li><Link to="/portal/events" className="hover:text-foreground">Events</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Support</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#" className="hover:text-foreground">Contact Us</a></li>
                <li><a href="#" className="hover:text-foreground">FAQ</a></li>
                <li><a href="#" className="hover:text-foreground">Terms of Service</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Contact</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>Addis Ababa, Ethiopia</li>
                <li>+251 911 123 456</li>
                <li>info@hmshotels.com</li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t text-center text-sm text-muted-foreground">
            © {new Date().getFullYear()} HMS Hotels. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
